import { PropsWithChildren } from 'react';
import { createPortal } from 'react-dom';
import { css } from '@emotion/react';
import { Box, useMediaQuery, useTheme } from '@mui/material';

import ContentQRBuilderHeader from '~/components/builder/ContentQRBuilderHeader';
import DesktopBuilderPreviewV2 from '~/components/builder/DesktopBuilderPreviewV2';
import MobileBuilderPreviewV2 from '~/components/builder/MobileBuilderPreviewV2';
import NameAndOrganizationBlock from '~/components/builder/NameAndOrganizationBlock';
import PasswordProtectionBlock from '~/components/builder/PasswordProtectionBlock';
import QrTypeTopCard from '~/components/builder/QrTypeTopCard';
import SettingsBlockV2 from '~/components/builder/SettingsBlockV2';
import { DESKTOP_MIN_WIDTH } from '~/constants/common';
import { PATH } from '~/constants/route-path';
import useBuilderBottomNavigationElement from '~/hooks/useBuilderBottomNavigationElement';
import { useAppDispatch } from '~/hooks/useRedux';
import NavigationBottom from '~/pages/CreateQR/components/NavigationBottom';
import { stepperSlice } from '~/store/reducers/stepepr';
import useBuilderStore from '~/store/useBuilderStore';
import { DynamicQrCodeType } from '~/types/builder';

const stepperActions = stepperSlice.actions;

type BuilderSecondStepLayoutProps = {
  onNext: () => void;
  qrType: DynamicQrCodeType;
  isNextProcessing: boolean;
};

export default function BuilderSecondStepLayout({
  children,
  onNext,
  qrType,
  isNextProcessing,
}: PropsWithChildren<BuilderSecondStepLayoutProps>) {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_MIN_WIDTH));

  const styles = useStyles();
  const { activeStep } = useBuilderStore();

  const { bottomNavigationElement } = useBuilderBottomNavigationElement();

  const handleBack = () => {
    if (activeStep === 1) {
      dispatch(stepperActions.setRedirectRoute(PATH.QR.CREATE));
    }
  };

  if (isDesktop) {
    return (
      <Box sx={styles.container}>
        <Box sx={styles.left}>
          <ContentQRBuilderHeader />
          <QrTypeTopCard type={qrType} />
          {children}
          <NameAndOrganizationBlock />
          <PasswordProtectionBlock />
          <SettingsBlockV2 />
          {bottomNavigationElement &&
            createPortal(
              <NavigationBottom
                isValid
                handleNext={onNext}
                isNextProcessing={isNextProcessing}
                handleBack={handleBack}
              />,
              bottomNavigationElement,
            )}
        </Box>
        <DesktopBuilderPreviewV2 />
      </Box>
    );
  }

  return (
    <>
      <ContentQRBuilderHeader />
      <QrTypeTopCard type={qrType} />
      {children}
      <MobileBuilderPreviewV2 />
      <NameAndOrganizationBlock />
      <PasswordProtectionBlock />
      <SettingsBlockV2 />
    </>
  );
}

function useStyles() {
  return {
    container: css`
      display: flex;
      gap: 24px;
    `,
    left: css`
      flex: 1;
    `,
  };
}
